import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { X, BookOpen, ChevronDown } from "lucide-react";
import { getClass, createClass, updateClass } from "../../services/classes";
import { getAcademicTerms } from "../../services/academicTerms";

interface SimpleClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId?: string;
}

const SimpleClassModal: React.FC<SimpleClassModalProps> = ({
  isOpen,
  onClose,
  classId,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    grade: "",
    capacity: "",
    section: "",
    academicTermId: "current", // Default academic term
  });

  // Fetch class data for editing
  const { data: classResponse, isLoading } = useQuery({
    queryKey: ["class", classId],
    queryFn: () => getClass(classId!),
    enabled: isOpen && !!classId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch academic terms for dropdown
  const { data: academicTermsResponse } = useQuery({
    queryKey: ["academicTerms"],
    queryFn: getAcademicTerms,
    enabled: isOpen,
    staleTime: 10 * 60 * 1000, // 10 minutes - academic terms don't change often
    refetchOnWindowFocus: false,
  });

  const classData = classResponse?.class;
  const academicTerms = academicTermsResponse?.data || [];

  // Update form data when class data is loaded
  useEffect(() => {
    if (classData) {
      setFormData({
        name: classData.name || "",
        grade: classData.grade || "",
        capacity: classData.capacity?.toString() || "",
        section: classData.section || "",
        academicTermId: classData.academicTerm || "current",
      });
    } else if (!classId) {
      // Reset form for new class
      setFormData({
        name: "",
        grade: "",
        capacity: "",
        section: "",
        academicTermId: "current",
      });
    }
  }, [classData, classId]);

  // Create class mutation
  const createMutation = useMutation({
    mutationFn: createClass,
    onSuccess: () => {
      toast.success("Class created successfully");
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to create class");
    },
  });

  // Update class mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateClass(id, data),
    onSuccess: () => {
      toast.success("Class updated successfully");
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to update class");
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.grade || !formData.capacity) {
      toast.error("Please fill in all required fields");
      return;
    }

    const submitData = {
      name: formData.name,
      grade: formData.grade,
      capacity: parseInt(formData.capacity),
      section: formData.section || undefined,
      academicTermId: formData.academicTermId,
    };

    if (classId) {
      updateMutation.mutate({ id: classId, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-[9998]"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-[10000]">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {classId ? "Edit Class" : "Create Class"}
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6">
            {isLoading && classId ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  Loading class data...
                </span>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    {classId
                      ? "Edit the class information below."
                      : "Fill in the details to create a new class."}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Class Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2 border"
                      placeholder="Enter class name (e.g., Math Class A)"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="grade"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Grade *
                    </label>
                    <input
                      type="text"
                      id="grade"
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2 border"
                      placeholder="Enter grade (e.g., 10th Grade, Form 4)"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="section"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Section
                    </label>
                    <input
                      type="text"
                      id="section"
                      name="section"
                      value={formData.section}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2 border"
                      placeholder="Enter section (e.g., A, B, East Wing)"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="academicTermId"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Academic Term *
                    </label>
                    <div className="relative">
                      <select
                        id="academicTermId"
                        name="academicTermId"
                        value={formData.academicTermId}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2 border appearance-none bg-white"
                        required
                      >
                        <option value="">
                          {academicTerms.length === 0
                            ? "No academic terms available - Contact admin"
                            : "Select Academic Term"}
                        </option>
                        {academicTerms.map((term: any) => (
                          <option key={term._id} value={term._id}>
                            {term.name} {term.isActive ? "(Active)" : ""}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="capacity"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Capacity *
                    </label>
                    <input
                      type="number"
                      id="capacity"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2 border"
                      placeholder="Maximum number of students"
                      min="1"
                      max="200"
                      required
                    />
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                createMutation.isPending ||
                updateMutation.isPending ||
                (isLoading && !!classId)
              }
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : classId
                ? "Save Changes"
                : "Create Class"}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleClassModal;
